/**
 * The main factory's assembly bay — where drones and mechs get built.
 *
 * Spec §7 and §9 price drones and mechs in goods and say nothing about who makes
 * them or how long it takes, so buying one used to be instantaneous: pay, and it
 * exists. That made the factory a wallet rather than a place.
 *
 * It builds them now, one at a time, over real ticks. Three things follow, and
 * all three are the point:
 *
 *   The bay is contested. A mech ordered now is a mech not arriving for forty
 *   ticks, and it is forty ticks a heavy drone did not get.
 *
 *   Early fleet decisions bind. Ordering three heavies before the first mech is
 *   a hundred and eighty ticks of no construction capacity, which is a mistake
 *   the player can make and recover from rather than a slider they cannot get
 *   wrong.
 *
 *   Factory districts do not help. They raise component throughput; assembling
 *   finished machines is the main factory's own work. The manufacturing plants
 *   that will eventually take it over are a later structure.
 *
 * Materials are consumed when an order ENTERS the bay, not when it is placed —
 * the same rule the city build queue follows, so an order waiting in line costs
 * nothing and can be cancelled for free.
 */

import {
  ASSEMBLY_QUEUE_LIMIT,
  ASSEMBLY_TICKS_DRONE_HEAVY,
  ASSEMBLY_TICKS_DRONE_STANDARD,
  ASSEMBLY_TICKS_MECH,
  MECH_COST,
} from './constants';
import { DRONE_CLASSES_DATA, GOOD_IDS, type DroneClass, type GoodId } from './data';
import type { AssemblyOrder, GameState } from './types';

export type UnitCost = Partial<Record<GoodId, number>>;

export function canAfford(state: GameState, cost: UnitCost): boolean {
  for (const id of GOOD_IDS) {
    const required = cost[id];
    if (required === undefined) continue;
    if (state.inventory.goods[id] < required) return false;
  }
  return true;
}

function spend(state: GameState, cost: UnitCost): void {
  for (const id of GOOD_IDS) {
    const required = cost[id];
    if (required === undefined) continue;
    state.inventory.goods[id] -= required;
  }
}

/** What an order costs in goods. */
export function assemblyCost(order: AssemblyOrder): UnitCost {
  if (order.unit === 'mech') return MECH_COST;
  return DRONE_CLASSES_DATA[order.class ?? 'standard'].cost;
}

/** How long an order occupies the bay. */
export function assemblyTicks(order: AssemblyOrder): number {
  if (order.unit === 'mech') return ASSEMBLY_TICKS_MECH;
  return (order.class ?? 'standard') === 'heavy'
    ? ASSEMBLY_TICKS_DRONE_HEAVY
    : ASSEMBLY_TICKS_DRONE_STANDARD;
}

/**
 * Place an order. Returns false if the queue is full.
 *
 * Nothing is checked against inventory here: affordability is decided when the
 * order reaches the bay, so a player can queue ahead of production the same way
 * they can with the city.
 */
export function orderUnit(state: GameState, order: AssemblyOrder): boolean {
  if (state.factory.assemblyQueue.length >= ASSEMBLY_QUEUE_LIMIT) return false;
  state.factory.assemblyQueue.push({
    unit: order.unit,
    ...(order.class === undefined ? {} : { class: order.class }),
  });
  return true;
}

/** Drop a waiting order. The one in the bay has already been paid for. */
export function cancelAssemblyOrder(state: GameState, index: number): void {
  if (index < 0 || index >= state.factory.assemblyQueue.length) return;
  state.factory.assemblyQueue.splice(index, 1);
}

function completeAssembly(state: GameState, job: AssemblyOrder): void {
  if (job.unit === 'mech') {
    state.mechs.total += 1;
    return;
  }

  const droneClass: DroneClass = job.class ?? 'standard';
  const spec = DRONE_CLASSES_DATA[droneClass];
  state.drones.push({
    id: state.nextDroneId,
    class: droneClass,
    capacity: spec.capacity,
    speedKmPerTick: spec.speedKmPerTick,
    assignedHex: null,
    phase: 'idle',
    phaseDeciTicksRemaining: 0,
    cargo: null,
    autoReassign: false,
  });
  state.nextDroneId += 1;
}

/**
 * Advance the bay by one tick.
 *
 * Runs after construction, alongside the rest of the factory's work. An order
 * that cannot be afforded simply waits — it does not skip, and it does not
 * consume the bay, so a cheap order behind an expensive one is not blocked by
 * it the way the CITY queue deliberately is. Assembly is a convenience; the city
 * queue is a statement of intent, and only one of those earns head-of-line
 * blocking.
 */
export function advanceAssembly(state: GameState): void {
  const factory = state.factory;

  if (factory.assembly !== null) {
    factory.assembly.ticksRemaining -= 1;
    if (factory.assembly.ticksRemaining <= 0) {
      completeAssembly(state, factory.assembly);
      factory.assembly = null;
    }
    return;
  }

  // Bay is free: take the first order that can actually be paid for.
  for (let i = 0; i < factory.assemblyQueue.length; i++) {
    const order = factory.assemblyQueue[i];
    if (order === undefined) continue;
    const cost = assemblyCost(order);
    if (!canAfford(state, cost)) continue;

    spend(state, cost);
    factory.assemblyQueue.splice(i, 1);
    const totalTicks = assemblyTicks(order);
    factory.assembly = {
      unit: order.unit,
      ...(order.class === undefined ? {} : { class: order.class }),
      ticksRemaining: totalTicks,
      totalTicks,
    };
    return;
  }
}
